import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 1rem;
  margin-top: 2em;
  color: ${({ theme }) => theme.colors.accent};
`;

const Section = styled.section`
  margin: 2rem 0;
`;

const H2 = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const H3 = styled.h3`
  font-size: 1.2rem;
  margin: 1rem 0 0.5rem;
`;

const P = styled.p`
  margin: 0.5rem 0;
`;

const List = styled.ul`
  margin: 0.5rem 0 0.5rem 1.25rem;
`;

const Code = styled.span`
  background: rgba(255,255,255,0.06);
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
`;

const WhitepaperPage = () => {
  return (
    <Wrapper>
      <Helmet>
        <title>YooWin White Paper</title>
      </Helmet>

      <Title>YooWin White Paper</Title>

      <Section>
        <H2>🎯 Executive Summary</H2>
        <P>YooWin is a revolutionary on-chain raffle platform that combines transparency, fairness, and gamification to deliver an unparalleled user experience. Built on Polygon for optimal speed and cost-efficiency, YooWin leverages blockchain technology to ensure every raffle is verifiable, trustless, and engaging.</P>
        <P>Our mission is to democratize access to fair gaming while building a sustainable ecosystem that rewards participants, supports growth, and maintains operational excellence.</P>
      </Section>

      <Section>
        <H2>🌟 Vision & Mission</H2>
        <H3>Vision</H3>
        <P>To become the leading transparent raffle platform where users can participate with confidence, knowing that every draw is provably fair and recorded on-chain.</P>
        <H3>Mission</H3>
        <P>To deliver a gamified, accessible, and trustworthy raffle experience that empowers users through blockchain transparency while building a sustainable platform for long-term growth.</P>
      </Section>

      <Section>
        <H2>💰 Revenue Distribution Model</H2>
        <H3>Distribution Breakdown</H3>
        <P><strong>70% → Customer (Winner)</strong> The majority of funds staked in each raffle go directly to the winner, ensuring substantial rewards and maintaining user trust and excitement.</P>
        <P>30% → YooWin Platform The remaining 30% is strategically allocated to ensure platform sustainability and growth:</P>
        <List>
          <li><strong>10% → Marketing Pocket</strong><br/>Dedicated to the media team for user acquisition, brand awareness, community engagement, and promotional campaigns.</li>
          <li><strong>60% → YooWin Team</strong><br/>Allocated for team compensation, development, innovation, and platform enhancement to ensure continuous improvement.</li>
          <li><strong>30% → System Operations</strong><br/>Covers infrastructure costs, smart contract gas fees, security audits, server maintenance, and operational expenses.</li>
        </List>
        <H3>Why This Model Works</H3>
        <List>
          <li>User-First Approach: 70% payout ensures competitive rewards that attract and retain users</li>
          <li>Sustainable Growth: Marketing allocation drives user acquisition and platform visibility</li>
          <li>Quality Development: Team allocation ensures top-tier talent and continuous innovation</li>
          <li>Reliable Operations: System allocation guarantees platform stability and security</li>
        </List>
      </Section>

      <Section>
        <H2>🎮 Platform Features</H2>
        <H3>Core Functionality</H3>
        <List>
          <li>Bi-Hourly Raffles: Automated games every 2 hours for consistent engagement</li>
          <li>Commit-Reveal Mechanism: Ensures provably fair winner selection</li>
          <li>On-Chain Verification: All game data recorded on Polygon blockchain</li>
          <li>XP Reward System: Gamified participation incentives</li>
          <li>Wallet-Only Access: No login required, just pay and play</li>
        </List>
        <H3>User Experience</H3>
        <List>
          <li>Vintage TV Interface: Nostalgic, engaging design inspired by classic game shows</li>
          <li>Real-Time Countdown: Live tracking of active and upcoming raffles</li>
          <li>Transparent History: Public access to all past winners and game records</li>
          <li>Mobile Optimized: Seamless experience across all devices</li>
        </List>
      </Section>

      <Section>
        <H2>🔐 Security & Transparency</H2>
        <H3>Smart Contract Security</H3>
        <List>
          <li>Audited Solidity contracts with transparent logic</li>
          <li>Non-custodial design: contracts don't hold user funds</li>
          <li>Immutable record of all games and winners</li>
          <li>Open-source code for community verification</li>
        </List>
        <H3>Randomness & Fairness</H3>
        <List>
          <li>Phase 1: Commit-reveal mechanism with backend seed generation</li>
          <li>Phase 2: Chainlink VRF integration for trustless randomness</li>
          <li>Publicly verifiable winner selection process</li>
          <li>On-chain audit trail for every draw</li>
        </List>
      </Section>

      <Section>
        <H2>📊 Market Opportunity</H2>
        <H3>Target Market</H3>
        <List>
          <li>Primary: Gen Z crypto enthusiasts (18-28 years old)</li>
          <li>Secondary: Blockchain gaming community</li>
          <li>Geographic: Global, with focus on crypto-friendly regions</li>
        </List>
        <H3>Competitive Advantages</H3>
        <List>
          <li>Full Transparency: Unlike traditional raffles, every aspect is verifiable on-chain</li>
          <li>Low Barrier to Entry: Accessible pricing and simple user experience</li>
          <li>Generous Payouts: 70% distribution is highly competitive</li>
          <li>Gamification: XP system and engaging interface drive retention</li>
          <li>Fast & Cheap: Polygon network ensures minimal gas fees</li>
        </List>
      </Section>

      <Section>
        <H2>🛣 Roadmap</H2>
        <H3>Phase 1: Testnet Launch (Month 1)</H3>
        <List>
          <li>Deploy on Mumbai Testnet</li>
          <li>Community testing and feedback collection</li>
          <li>Bug bounty program</li>
          <li>Smart contract audit</li>
          <li>Marketing campaign initiation</li>
        </List>
        <H3>Phase 2: Mainnet Launch (Month 2-3)</H3>
        <List>
          <li>Deploy on Polygon Mainnet</li>
          <li>USDT payout integration</li>
          <li>Full platform launch</li>
          <li>Influencer partnerships</li>
          <li>User acquisition campaigns</li>
        </List>
        <H3>Phase 3: Expansion (Month 4-6)</H3>
        <List>
          <li>Chainlink VRF integration</li>
          <li>NFT-based tiers and bonuses</li>
          <li>Referral and affiliate system</li>
          <li>Multi-language support</li>
          <li>Mobile app development</li>
        </List>
        <H3>Phase 4: Ecosystem Growth (Month 7-12)</H3>
        <List>
          <li>YooWin token launch</li>
          <li>XP-to-Token conversion mechanism</li>
          <li>DAO governance implementation</li>
          <li>Cross-chain expansion</li>
          <li>Strategic partnerships</li>
        </List>
      </Section>

      <Section>
        <H2>💼 Team & Governance</H2>
        <H3>Team Allocation (60% of 30%)</H3>
        <P>The YooWin team is composed of experienced blockchain developers, designers, and growth specialists committed to building a world-class platform.</P>
        <List>
          <li>Development: Smart contract engineers, full-stack developers</li>
          <li>Design: UI/UX designers, brand specialists</li>
          <li>Operations: Community managers, customer support</li>
          <li>Growth: Business development, partnerships</li>
        </List>
        <H3>Future Governance</H3>
        <P>YooWin will transition to a DAO model, allowing token holders to vote on:</P>
        <List>
          <li>Raffle duration and frequency</li>
          <li>Payout distribution adjustments</li>
          <li>Platform feature prioritization</li>
          <li>Treasury management</li>
        </List>
      </Section>

      <Section>
        <H2>📈 Growth Strategy</H2>
        <H3>Marketing (10% of 30%)</H3>
        <List>
          <li>Social Media: Twitter, Discord, Telegram community building</li>
          <li>Influencer Partnerships: Crypto influencers and gaming streamers</li>
          <li>Content Marketing: Educational content about fair gaming and blockchain</li>
          <li>Paid Advertising: Targeted campaigns on crypto platforms</li>
          <li>PR & Media: Coverage in crypto news outlets</li>
        </List>
        <H3>User Acquisition</H3>
        <List>
          <li>Launch promotions and bonus XP campaigns</li>
          <li>Referral incentives</li>
          <li>Community contests and giveaways</li>
          <li>Strategic partnerships with Web3 projects</li>
        </List>
      </Section>

      <Section>
        <H2>💡 Innovation & Technology</H2>
        <H3>Technical Stack</H3>
        <List>
          <li>Blockchain: Polygon (Mumbai Testnet → Mainnet)</li>
          <li>Smart Contracts: Solidity with OpenZeppelin libraries</li>
          <li>Backend: Node.js, Express, MongoDB</li>
          <li>Frontend: React, Web3.js, Ethers.js</li>
          <li>Payment: OxaPay integration</li>
        </List>
        <H3>Future Innovations</H3>
        <List>
          <li>Chainlink VRF for verifiable randomness</li>
          <li>The Graph integration for efficient querying</li>
          <li>IPFS for decentralized data storage</li>
          <li>Multi-chain expansion (Arbitrum, Optimism)</li>
        </List>
      </Section>

      <Section>
        <H2>⚖️ Legal & Compliance</H2>
        <P>YooWin is committed to operating within legal frameworks:</P>
        <List>
          <li>Compliance with gaming and gambling regulations in target jurisdictions</li>
          <li>KYC/AML procedures for high-value transactions (where required)</li>
          <li>Terms of Service and Privacy Policy transparency</li>
          <li>Age verification mechanisms</li>
          <li>Responsible gaming features and limits</li>
        </List>
      </Section>

      <Section>
        <H2>🎯 Key Metrics & Success Indicators</H2>
        <H3>User Metrics</H3>
        <List>
          <li>Daily Active Users (DAU)</li>
          <li>Monthly Active Users (MAU)</li>
          <li>User retention rate</li>
          <li>Average participation frequency</li>
        </List>
        <H3>Financial Metrics</H3>
        <List>
          <li>Total Value Locked (TVL)</li>
          <li>Revenue per raffle</li>
          <li>User acquisition cost (UAC)</li>
          <li>Lifetime value (LTV)</li>
        </List>
        <H3>Engagement Metrics</H3>
        <List>
          <li>XP earned and redeemed</li>
          <li>Social media engagement</li>
          <li>Community growth rate</li>
          <li>Referral conversion rate</li>
        </List>
      </Section>

      <Section>
        <H2>🌍 Social Impact</H2>
        <P>YooWin is more than just a raffle platform—it's a movement toward transparent, fair gaming:</P>
        <List>
          <li>Financial Inclusion: Low entry barriers enable global participation</li>
          <li>Transparency: Blockchain technology eliminates traditional gaming opacity</li>
          <li>Education: Introducing users to Web3 technology and blockchain benefits</li>
          <li>Community: Building a supportive ecosystem of fair gaming enthusiasts</li>
        </List>
      </Section>

      <Section>
        <H2>🔮 Conclusion</H2>
        <P>YooWin represents the future of fair, transparent, and engaging raffle experiences. By leveraging blockchain technology, maintaining a generous 70% payout to winners, and reinvesting strategically in marketing, team development, and system operations, we are building a sustainable platform that benefits all stakeholders.</P>
        <P>Our commitment to transparency, security, and user experience positions YooWin as the premier destination for on-chain gaming. With a clear roadmap, innovative features, and a dedicated team, we invite you to join us in revolutionizing the raffle industry.</P>
        <P><em>Your Game. Your Win. Your Future.</em></P>
      </Section>

      <Section>
        <H2>📞 Get Involved</H2>
        <List>
          <li>Website: <Code>yoowin.online</Code></li>
          <li>Twitter: <Code>@YooWinApp</Code></li>
          <li>Discord: Coming soon</li>
          <li>Email: <Code>info@yoowin.online</Code></li>
          <li>Testnet: Test on Mumbai</li>
        </List>
        <P>🎉 Let's build the future of fair gaming together!</P>
      </Section>
    </Wrapper>
  );
};

export default WhitepaperPage;
